<script lang="ts" setup>
import { VBtn, VToolbar } from 'vuetify/components'
import ConsulttedLogo from '@/assets/brand_assets/SVG/logo-consultted-icon.svg'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { usePatientStore } from '@/stores/patient'
import router from '@/router'
import { navigateToPage } from '@/util/common'
import { useScreenStore } from '@/stores/screen'

const burgerMenuOpen = ref(false)
const burgerMenuRef = ref<HTMLElement | null>(null)

const patientStore = usePatientStore()
const screenStore = useScreenStore()

const toggleBurgerMenu = () => {
    burgerMenuOpen.value = !burgerMenuOpen.value
}

const handleClickOutside = (event: MouseEvent) => {
    if (burgerMenuRef.value && !burgerMenuRef.value.contains(event.target as Node)) {
        burgerMenuOpen.value = false
    }
}

const handleLogout = async () => {
    await patientStore.logout()
    router.push('/login')
}

const buttonConfigs = [
    {
        id: 'personalProfile',
        text: 'Personal Profile',
        color: 'secondary',
        prependIcon: 'mdi-account',
        click: () => navigateToPage('patient-profile'),
    },
    {
        id: 'consultationHistory',
        text: 'Consultation History',
        color: 'secondary',
        prependIcon: 'mdi-account',
        click: () => navigateToPage('consultation-history'),
    },
    // {
    //     id: 'grabAndGo',
    //     text: 'Grab and Go Advice packs',
    //     color: 'secondary',
    //     prependIcon: 'mdi-account',
    //     click: () => navigateToPage('grab-and-go')
    // },
    {
        id: 'settings',
        text: 'Settings',
        color: 'secondary',
        prependIcon: 'mdi-cog',
        click: () => navigateToPage('setting-view'),
    },
    {
        id: 'logout',
        text: 'Logout',
        color: 'secondary',
        prependIcon: 'mdi-logout',
        click: () => handleLogout(),
    },
]

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
    <v-app>
        <v-toolbar class="default-app-bar" :color="screenStore.isWideOrLandscape ? 'white' : 'secondary'">
            <div class="consultted-title-logo" @click="navigateToPage('dashboard')">
                <img :src="ConsulttedLogo" alt="ConsultTed Logo" width="64rem" />
            </div>

            <template #append>
                <div class="default-app-bar-append-container">
                    <div class="default-app-bar-buttons-container">
                        <!-- HOME BUTTON -->
                        <v-btn class="default-app-bar-home-button mr-3" flat variant="elevated" color="primary" @click.stop="navigateToPage('dashboard')">
                            <v-icon size="24">mdi-home</v-icon>
                        </v-btn>
                        <!-- BURGER MENU BUTTON -->
                        <div v-if="patientStore.isAuthenticated" class="burger-menu-container" ref="burgerMenuRef">
                            <v-btn class="default-app-bar-burger-button" flat variant="elevated" color="primary" @click.stop="toggleBurgerMenu">
                                <v-icon size="24">{{ burgerMenuOpen ? 'mdi-close' : 'mdi-menu' }}</v-icon>
                            </v-btn>

                            <transition name="fade">
                                <div v-if="burgerMenuOpen" class="custom-burger-menu">
                                    <v-list>
                                        <v-list-item
                                            v-for="btn in buttonConfigs"
                                            :key="btn.id"
                                            @click="
                                                () => {
                                                    btn.click()
                                                    burgerMenuOpen = false
                                                }
                                            "
                                        >
                                            <template #prepend>
                                                <v-icon class="custom-burger-menu-icon">{{ btn.prependIcon }}</v-icon>
                                            </template>
                                            <v-list-item-title>{{ btn.text }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>
            </template>
        </v-toolbar>
        <v-main class="bg-grey-lighten-2">
            <router-view />
        </v-main>
    </v-app>
</template>

<style>
.default-app-bar,
.v-toolbar__content,
.v-toolbar__extension {
    overflow: visible !important;
}

.v-toolbar-title {
    flex: none;
    margin-right: 2em;
}

.fill-block {
    line-height: 64px;
}

.consultted-title-logo {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    width: 10rem;
    height: auto;
    margin-left: 1rem;
}

.cls-1 {
    fill: #ffcf04;
}

.cls-2 {
    fill: #262626;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.default-app-bar-append-container {
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: visible;
}

.default-app-bar-button {
    margin-right: 0.5rem;
}

.default-app-bar-burger-button {
    margin-right: 0.5rem;
}

.page-title {
    margin-left: 2rem;
    font-weight: 700;
}

.burger-menu-container {
    position: relative;
    display: inline-block;
}

.custom-burger-menu {
    position: absolute;
    top: 100%;
    right: 0;
    width: max-content;
    height: auto;
    background-color: #ffffff;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    padding: 0.5rem;
    z-index: 9999;
    overflow-y: auto;
}

.custom-burger-menu-icon {
    margin: 0;
    padding: 0;
}

/* Optional fade animation */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
